




















import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import List from '@/components/util/List.vue';
import DragUpload from '@/components/util/DragUpload.vue';

import { ColumnDisplayOption } from '@/models/ColumnDisplayOption';
import { SortChangedEvent } from '@/models/SortChangedEvent';

import { DocumentsApi, Document, QueryDocumentsRequest } from 'performo-api';
import { mapGetters } from 'vuex';
import { User } from 'oidc-client';

@Component({
  components: {
    List,
    DragUpload
  },
  computed: mapGetters({
    currentPropertyId: 'currentPropertyId',
    currentUser: 'currentUser',
    api: 'documentsApi'
  })
})
export default class DocumentListView extends Vue {
  @Prop({ required: true }) query!: QueryDocumentsRequest;
  @Prop({ required: false }) columnDisplay!: ColumnDisplayOption[];
  @Prop({ required: false, default: false }) doPagination!: boolean;
  @Prop({ required: false, default: false }) updateRouteWithQuery!: boolean;
  @Prop({ required: false, default: 'No documents to display.' })
  zeroCountDisplayMessage!: string;

  @Prop({ required: false, type: Number, default: 25 }) pageSize!: number;

  private api!: DocumentsApi;
  private documents?: Document[];
  private internalPageNumber = 0;

  private currentPropertyId!: string;
  private currentUser!: User;

  private loading = false;

  data() {
    return {
      documents: undefined,
      outputModeOverride: undefined
    };
  }

  get columnDisplayComputed(): ColumnDisplayOption[] {
    // this computed value uses the incoming prop if defined, but if not uses a sensible default.
    return this.columnDisplay
      ? this.columnDisplay
      : [
          { displayName: 'Title', columnName: 'docTitle', dontSort: true },
          { displayName: 'Type', columnName: 'docType', dontSort: true },
          {
            displayName: 'Version',
            columnName: 'currentVersion',
            dontSort: true
          },
          { displayName: 'Status', columnName: 'docStatus', dontSort: true }
        ];
  }

  private outputModeOverride!: string;

  get documentsQuery() {
    return this.query || {};
  }

  private sortChangedHandler(sort: SortChangedEvent) {
    this.$emit('sort-changed', sort);

    // reset to first page
    this.documentsQuery.skip = 0;
    this.internalPageNumber = 0;

    // sort changed, re-fetch the data from the server
    this.fetchDocuments();
  }

  private goPageForward() {
    // do page forward things
    this.internalPageNumber += 1;
    this.paginationChangeHandler(this.internalPageNumber);
  }

  private goPageBackward() {
    // do page forward things
    this.internalPageNumber -= 1;
    this.paginationChangeHandler(this.internalPageNumber);
  }

  private paginationChangeHandler(pageNumber: number) {
    this.documentsQuery.skip = this.pageSize * pageNumber + 1;

    this.fetchDocuments();
  }

  private listHeaderClickedHandler(item: ColumnDisplayOption) {
    this.$emit('document-header-clicked', item);
  }

  private async listRowClickedHandler(item: Document) {
    this.$emit('document-clicked', item);
    const docsApi = new DocumentsApi(this.$store.getters.getApiConfiguration);
    if (item.documentId && item.currentVersion) {
      const doc = await docsApi.getDocumentVersion({
        documentId: item.documentId,
        version: item.currentVersion
      });
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = 'data:application/octet-stream;base64,' + doc.containerData;
      // the filename you want
      a.download = doc.containerFileName
        ? doc.containerFileName
        : 'downloaded-file';
      document.body.appendChild(a);
      a.click();
    }
  }

  @Watch('documentsQuery', { deep: true })
  private async queryChanged(val: QueryDocumentsRequest) {
    if (val) {
      val.skip = 0; // always reset to beginning
      this.internalPageNumber = 0;
      this.fetchDocuments();
    }
  }

  private async fetchDocuments() {
    if (this.documentsQuery) {
      const query = {
        ...this.documentsQuery,
        skip: this.documentsQuery.skip ? this.documentsQuery.skip : 0,
        take: this.documentsQuery.take
          ? this.documentsQuery.take
          : this.pageSize
      };

      this.loading = true;
      this.api
        .queryDocuments(query)
        .then((d) => (this.documents = d))
        .catch(() => {
          /*noop*/
        })
        .finally(() => (this.loading = false));
    }
  }
}
